import { CONFIG } from '@frontend/config';
import type {
  PublicQuoteSelection,
  PublicQuoteView,
  PublicQuotePricing,
  PublicQuoteSubmission,
} from '@bandido/shared/types';
export interface PublicQuoteEvaluation {
  versionId: string;
  archived: boolean;
  updated: boolean;
  pricing: PublicQuotePricing;
  alternatives: Array<{ optionId: string; deltaMinor: number | null }>;
}
/** Deliberately excludes CRM cookies, authorization, agency headers and refresh interceptors. */
export async function publicQuoteRequest<T>(
  token: string,
  path = '',
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(
    `${CONFIG.API_URL.replace(/\/?$/, '/')}public/commercial-quotes${path}?token=${encodeURIComponent(token)}`,
    {
      method: body ? 'POST' : 'GET',
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal,
    },
  );
  if (!response.ok) throw new Error(String(response.status));
  return response.json();
}
export const fetchPublicQuote = (token: string, signal?: AbortSignal, versionId?: string) =>
  publicQuoteRequest<PublicQuoteView>(
    token,
    versionId ? `/versions/${encodeURIComponent(versionId)}` : '',
    undefined,
    signal,
  );
export const evaluatePublicQuote = (
  token: string,
  versionId: string,
  selection: PublicQuoteSelection,
  blockId?: string,
  signal?: AbortSignal,
) => publicQuoteRequest<PublicQuoteEvaluation>(token, '/evaluate', { versionId, selection, blockId }, signal);

export const submitPublicQuote = (
  token: string,
  versionId: string,
  selection: PublicQuoteSelection,
  requestId: string,
  signal?: AbortSignal,
) => publicQuoteRequest<PublicQuoteSubmission>(token, '/selections', { versionId, selection, requestId }, signal);

export async function downloadPublicQuote(
  token: string,
  versionId: string,
  selection: PublicQuoteSelection,
  lang: 'es' | 'en',
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await fetch(
    `${CONFIG.API_URL.replace(/\/?$/, '/')}public/commercial-quotes/pdf?token=${encodeURIComponent(token)}`,
    {
      method: 'POST',
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ versionId, selection, lang }),
      signal,
    },
  );
  if (!response.ok || !response.headers.get('content-type')?.includes('application/pdf')) throw new Error(String(response.status));
  return response.blob();
}
